import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert } from 'react-bootstrap'
import RjsfForm from '@rjsf/core'
import type { RJSFSchema } from '@rjsf/utils'
import { Button } from '../../Button/Button'
import { applyMergePatch, buildMergePatch, pruneUndefined, type JsonObject } from './mergePatch'
import { buildUiSchema } from './uiSchema'
import { parametersValidator } from './rjsf/validator'
import { parameterTemplates } from './rjsf/templates'
import { ZocksReferenceWidget } from './referenceWidgets/ZocksReferenceWidget'

export interface WorkflowParametersFormProps {
  schema: Record<string, unknown>
  baseline: Record<string, unknown>
  layer: Record<string, unknown> | null
  saving: boolean
  onSave: (patch: Record<string, unknown>) => Promise<boolean>
  onReset: () => Promise<boolean>
  onDirtyChange: (dirty: boolean) => void
  externallyUpdated: boolean
  onAcknowledgeExternalUpdate: () => void
  idPrefix?: string
  layerName: 'account' | 'user'
}

export const WorkflowParametersForm: React.FC<WorkflowParametersFormProps> = ({
  schema,
  baseline,
  layer,
  saving,
  onSave,
  onReset,
  onDirtyChange,
  externallyUpdated,
  onAcknowledgeExternalUpdate,
  idPrefix = 'root',
  layerName,
}) => {
  const [trackedBaseline, setTrackedBaseline] = useState(baseline)
  const [trackedLayer, setTrackedLayer] = useState(layer)
  const [baselineSnapshot, setBaselineSnapshot] = useState<Record<string, unknown>>(baseline)
  const [initialFormData, setInitialFormData] = useState<JsonObject>(() => applyMergePatch(baseline, layer ?? {}) as JsonObject)
  const [formData, setFormData] = useState<JsonObject>(initialFormData)
  const [confirmingReset, setConfirmingReset] = useState(false)
  const [uploadingFieldIds, setUploadingFieldIds] = useState<Set<string>>(new Set())

  const uiSchema = useMemo(() => buildUiSchema(schema), [schema])

  const setFieldUploading = useCallback((fieldId: string, uploading: boolean) => {
    setUploadingFieldIds((current) => {
      const next = new Set(current)
      if (uploading) {
        next.add(fieldId)
      } else {
        next.delete(fieldId)
      }
      return next
    })
  }, [])

  const dirty = Object.keys(buildMergePatch(initialFormData, formData)).length > 0
  const uploading = uploadingFieldIds.size > 0

  if ((baseline !== trackedBaseline || layer !== trackedLayer) && !dirty) {
    setTrackedBaseline(baseline)
    setTrackedLayer(layer)
    setConfirmingReset(false)
    setBaselineSnapshot(baseline)
    const reinitialized = applyMergePatch(baseline, layer ?? {}) as JsonObject
    setInitialFormData(reinitialized)
    setFormData(reinitialized)
  }

  useEffect(() => {
    onDirtyChange(dirty || uploading)
    return () => {
      onDirtyChange(false)
    }
  }, [dirty, uploading, onDirtyChange])

  const handleSubmit = () => {
    const snapshot = formData
    const savedBaseline = baseline
    const savedLayer = layer
    void onSave(buildMergePatch(baselineSnapshot, pruneUndefined(formData) as JsonObject)).then((success) => {
      if (success) {
        setInitialFormData(snapshot)
        setTrackedBaseline(savedBaseline)
        setTrackedLayer(savedLayer)
      }
    })
  }

  const handleResetConfirm = () => {
    setConfirmingReset(false)
    const currentBaseline = baseline
    const currentLayer = layer
    void onReset().then((success) => {
      if (success) {
        const reset = applyMergePatch(currentBaseline, {}) as JsonObject
        setInitialFormData(reset)
        setFormData(reset)
        setBaselineSnapshot(currentBaseline)
        setTrackedBaseline(currentBaseline)
        setTrackedLayer(currentLayer)
      }
    })
  }

  return (
    <div className='d-flex flex-column zocks-gap-2'>
      {externallyUpdated ? (
        <Alert variant='info' dismissible onClose={onAcknowledgeExternalUpdate}>
          Values were updated elsewhere — Save will apply your edits over the new values.
        </Alert>
      ) : null}
      <RjsfForm
        idPrefix={idPrefix}
        formContext={{ rootId: idPrefix ?? 'root', layer: layerName, setFieldUploading }}
        schema={schema as RJSFSchema}
        uiSchema={uiSchema}
        validator={parametersValidator}
        widgets={{ zocksReference: ZocksReferenceWidget }}
        templates={parameterTemplates}
        formData={formData}
        onChange={(event) => setFormData((event.formData ?? {}) as JsonObject)}
        onSubmit={handleSubmit}
        liveValidate={false}
        showErrorList={false}
        disabled={saving}
      >
        <div className='d-flex justify-content-end zocks-gap-2 pt-2'>
          {layer !== null ? (
            confirmingReset ? (
              <div className='d-flex align-items-center zocks-gap-2'>
                <span className='paragraph-medium'>Reset?</span>
                <Button type='button' variant='borderless' disabled={saving} onClick={() => setConfirmingReset(false)}>
                  Cancel
                </Button>
                <Button type='button' variant='danger' loading={saving} disabled={saving || uploading} onClick={handleResetConfirm}>
                  Confirm
                </Button>
              </div>
            ) : (
              <Button type='button' variant='borderless' disabled={saving || uploading} onClick={() => setConfirmingReset(true)}>
                Reset to inherited
              </Button>
            )
          ) : null}
          <Button variant='primary' type='submit' loading={saving} disabled={!dirty || saving || uploading}>
            Save
          </Button>
        </div>
      </RjsfForm>
    </div>
  )
}
